// ClipAudioPlayer provides lightweight audio-only playback for QA and Video Performance
// Direction screens, letting reviewers scrub, restart, and download translated clips
// without loading a full video player.
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import classes from "./ClipAudioPlayer.module.css";
import { Grid, IconButton } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import StopIcon from "@mui/icons-material/Stop";

const ClipAudioPlayer = forwardRef(
  ({ src, decible, onPlay, onEnd, showScrubBar = true }, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadedPercentage, setLoadedPercentage] = useState(0);

  const audioContext = useRef(null);
  const gainNode = useRef(null);
  const source = useRef(null);
  const audioBuffer = useRef(null);
  const startTime = useRef(0);
  const pauseTime = useRef(0);
  const animationRef = useRef(null);
  const rangeRef = useRef(null);

  // Memoize the loader to satisfy the hooks dependency check.
  const loadAudio = useCallback(async () => {
    // Reset loading indicators before fetching the audio source.
    setIsLoading(true);
    setIsLoaded(false);
    setLoadedPercentage(0);
    try {
      // Fetch the audio stream and validate the response.
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }

      let arrayBuffer;
      if (response.body) {
        // Capture content length (when available) to compute download progress.
        const contentLengthHeader = response.headers.get("Content-Length");
        const contentLength = contentLengthHeader
          ? parseInt(contentLengthHeader, 10)
          : null;

        // Read the stream in chunks to accumulate bytes and report progress.
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          if (value) {
            chunks.push(value);
            receivedLength += value.length;

            // Update loaded percentage only when total size is known.
            if (contentLength && contentLength > 0) {
              const progress = (receivedLength / contentLength) * 100;
              setLoadedPercentage(progress);
            }
          }
        }

        // Merge streamed chunks into a single contiguous buffer for decoding.
        const combinedBuffer = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          combinedBuffer.set(chunk, position);
          position += chunk.length;
        }

        arrayBuffer = combinedBuffer.buffer;
      } else {
        // Fallback when streaming is unavailable: read the full array buffer.
        arrayBuffer = await response.arrayBuffer();
      }

      // Decode audio data and prepare playback metadata.
      const decodedAudio = await audioContext.current.decodeAudioData(
        arrayBuffer
      );
      audioBuffer.current = decodedAudio;
      setDuration(decodedAudio.duration);
      setIsLoaded(true);
      setLoadedPercentage(100); // Set to 100% when fully loaded
    } catch (error) {
      console.error("Error loading audio:", error);
    } finally {
      // Ensure loading state is cleared regardless of outcome.
      setIsLoading(false);
    }
  }, [src]);

  useEffect(() => {
    audioContext.current = new (window.AudioContext ||
      window.webkitAudioContext)();
    gainNode.current = audioContext.current.createGain();
    gainNode.current.connect(audioContext.current.destination);

    loadAudio();

    return () => {
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, [loadAudio]);

  useEffect(() => {
    const updateTime = () => {
      if (isPlaying && audioBuffer.current) {
        const elapsed = audioContext.current.currentTime - startTime.current;
        const newCurrentTime = Math.min(elapsed + pauseTime.current, duration);
        setCurrentTime(newCurrentTime);

        if (newCurrentTime >= duration) {
          resetAudio();
          // Call the onEnd function when audio reaches the end
          if (onEnd) {
            onEnd();
          }
        } else {
          animationRef.current = requestAnimationFrame(updateTime);
        }
      }
    };

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateTime);
    } else {
      cancelAnimationFrame(animationRef.current);
    }

    return () => cancelAnimationFrame(animationRef.current);
  }, [isPlaying, duration, onEnd]);

  const resetAudio = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    pauseTime.current = 0;
    if (source.current) {
      source.current.stop();
      source.current = null;
    }
  };

  useEffect(() => {
    const updateRangeProgress = () => {
      if (rangeRef.current) {
        const progress = (currentTime / duration) * 100;
        rangeRef.current.style.setProperty("--range-progress", `${progress}%`);
        rangeRef.current.style.setProperty(
          "--range-loaded",
          `${loadedPercentage}%`
        );
      }
    };

    updateRangeProgress();
  }, [currentTime, duration, loadedPercentage]);

  const startPlayback = () => {
    if (!isLoaded || isPlaying) return;

    source.current = audioContext.current.createBufferSource();
    source.current.buffer = audioBuffer.current;
    source.current.connect(gainNode.current);
    source.current.start(0, pauseTime.current);
    startTime.current = audioContext.current.currentTime;

    if (onPlay) {
      onPlay();
    }

    setIsPlaying(true);
  };

  const pausePlayback = () => {
    if (!isPlaying) return;

    if (source.current) {
      source.current.stop();
      source.current = null;
    }

    pauseTime.current = currentTime;
    setIsPlaying(false);
  };

  const stopPlayback = () => {
    resetAudio();
  };

  const changeVolume = (newVolume) => {
    if (gainNode.current) {
      const gainValue = newVolume / 100;
      gainNode.current.gain.setValueAtTime(
        gainValue,
        audioContext.current.currentTime
      );
    }
  };

  const seek = (seekTime) => {
    if (!isLoaded) return;

    setCurrentTime(seekTime);
    pauseTime.current = seekTime;

    if (isPlaying) {
      if (source.current) {
        source.current.stop();
      }
      source.current = audioContext.current.createBufferSource();
      source.current.buffer = audioBuffer.current;
      source.current.connect(gainNode.current);
      source.current.start(0, seekTime);
      startTime.current = audioContext.current.currentTime;
    }
  };

  // Expose the same playback controls the component supported originally.
  useImperativeHandle(ref, () => ({
    play: () => {
      if (!isPlaying && isLoaded) startPlayback();
    },
    pause: () => {
      if (isPlaying) pausePlayback();
    },
    stop: () => {
      stopPlayback();
    },
    setVolume: changeVolume,
    seek: seek,
    getCurrentTime: () => currentTime,
    getDuration: () => duration,
    isPlaying: () => isPlaying,
    isLoaded: () => isLoaded,
  }));

  const formatTime = (time, decimalPlaces = 2) => {
    if (!Number.isFinite(time)) {
      return "0.00";
    }

    const clampedDecimalPlaces = Math.max(0, decimalPlaces);
    return Number(time).toFixed(clampedDecimalPlaces);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      // Extract filename from the src URL or use a default name
      const fileName = src.split("/").pop() || "audio.mp3";
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
      // You might want to show an error message to the user here
    }
  };

  //Volume control
  useEffect(() => {
    if (decible !== null && decible !== undefined) {
      const gain = Math.pow(10, decible / 20); // gain = 10^(dB / 20)
      const newVolume = 100 * gain;
      changeVolume(Math.round(newVolume));
    }
  }, [decible]);

  return (
    <Grid
      container
      item
      direction="row"
      alignItems="center"
      justifyContent="space-evenly"
      wrap="nowrap"
      bgcolor="grey"
      style={{ background: "#F1F3F4", borderRadius: "var(--radius-md)" }}
      xs={10}
    >
      <Grid
        item
        xs={1}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isLoading ? (
          <IconButton
            size="small"
            sx={{
              color: "black",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            disabled
          >
            <RefreshIcon sx={{ fontSize: "1.4rem" }} />
          </IconButton>
        ) : isPlaying ? (
          <IconButton
            size="small"
            onClick={stopPlayback}
            sx={{
              color: "black",
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <StopIcon sx={{ fontSize: "1.4rem" }} />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={startPlayback}
            disabled={!isLoaded}
            sx={{
              color: "black",
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: "1.4rem" }} />
          </IconButton>
        )}
      </Grid>
      <Grid
        item
        fontSize="var(--font-size-normal)"
        fontFamily="Livvic, sans-serif"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </Grid>

      {showScrubBar && (
        <Grid
          item
          xs={5}
          sx={{ flexGrow: 1, minWidth: 0 }}
        >
          <input
            ref={rangeRef}
            className={classes["custom-range-input"]}
            type="range"
            min="0"
            max={duration || 100}
            step={0.001}
            value={currentTime}
            onChange={(e) => seek(parseFloat(e.target.value))}
            disabled={!isLoaded}
          />
        </Grid>
      )}

      <Grid
        item
        xs={1}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconButton
          onClick={handleDownload}
          size="small"
          sx={{
            color: "black",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <DownloadIcon sx={{ fontSize: "1.4rem" }} />
        </IconButton>
      </Grid>
    </Grid>
  );
});

export default ClipAudioPlayer;
